// Shared discount-code apply/remove logic — used identically by the
// cart drawer and the full cart page (both render this same
// <cart-discount-form> via snippets/cart-discount-form.liquid), so
// there's exactly one implementation of "apply/remove a discount
// code" for the whole theme rather than two independent ones.
//
// Uses Shopify's real /cart/update.js `discount` parameter — the same
// native mechanism the full cart page and checkout already use. That
// parameter REPLACES the cart's whole set of applied discount codes
// on every call, so "adding" one means resubmitting the full list
// (existing codes + the new one); "removing" one means resubmitting
// the list with it filtered out. Active codes are never tracked in JS
// memory — always read fresh from this element's own
// data-active-codes, which is re-rendered by the server on every cart
// change, so this naturally stays in sync with the real cart state.
if (!customElements.get('cart-discount-form')) {
  customElements.define(
    'cart-discount-form',
    class CartDiscountForm extends HTMLElement {
      constructor() {
        super();

        this.addEventListener('submit', (evt) => {
          const form = evt.target.closest('.cart-notification-discount__field');
          if (!form) return;
          evt.preventDefault();
          const input = form.querySelector('.cart-notification-discount__input');
          this.applyDiscountCode(input.value);
        });

        this.addEventListener('click', (evt) => {
          const removeButton = evt.target.closest('.cart-notification-discount__remove');
          if (removeButton) {
            evt.preventDefault();
            this.removeDiscountCode(removeButton.dataset.discountTitle);
            return;
          }

          // Toggles the input/Apply button open/closed on every click —
          // they start hidden behind this plain "Discount" text link
          // (collapsed: true — see snippets/cart-discount-form.liquid)
          // so the drawer/cart page stay compact until the customer
          // explicitly asks for this, and clicking again puts it back
          // away rather than only ever opening. The toggle text itself
          // stays visible either way, reading as a small label above
          // the field — matches "the toggle can be visually integrated
          // with the discount area."
          const toggle = evt.target.closest('.cart-notification-discount__toggle');
          if (toggle) {
            evt.preventDefault();
            const wrap = this.querySelector('.cart-notification-discount__panel-wrap');
            const panel = this.querySelector('.cart-notification-discount__panel');
            if (!wrap || !panel) return;

            const isOpen = wrap.classList.contains('is-expanded');
            if (isOpen) {
              wrap.classList.remove('is-expanded');
              // hidden gets set once the collapse transition actually
              // finishes (not immediately) so the content doesn't just
              // vanish mid-animation; { once: true } since this only
              // ever needs to fire for THIS close, not future ones.
              wrap.addEventListener(
                'transitionend',
                () => {
                  if (!wrap.classList.contains('is-expanded')) panel.hidden = true;
                },
                { once: true }
              );
            } else {
              panel.hidden = false;
              // rAF so the browser registers hidden->visible before the
              // class add, otherwise the grid-template-rows transition
              // (0fr -> 1fr) can collapse into a single frame and just
              // snap open instead of animating.
              requestAnimationFrame(() => {
                wrap.classList.add('is-expanded');
                const input = panel.querySelector('.cart-notification-discount__input');
                if (input) input.focus();
              });
            }
          }
        });

        // Live, every-keystroke toggle (not on blur/submit) between the
        // Apply button's grey "empty" state and black "ready to submit"
        // state — purely reflects whether the input currently has text,
        // NOT whether a code is valid (that's only ever known after
        // Shopify's own response comes back, reported via the message
        // text in applyDiscountCode/updateDiscount below).
        this.addEventListener('input', (evt) => {
          const input = evt.target.closest('.cart-notification-discount__input');
          if (!input) return;
          const applyButton = this.querySelector('.cart-notification-discount__apply');
          if (!applyButton) return;
          applyButton.classList.toggle('cart-notification-discount__apply--ready', input.value.trim().length > 0);
        });
      }

      getSectionsToRender() {
        try {
          return JSON.parse(this.dataset.sections || '[]');
        } catch (error) {
          return [];
        }
      }

      // Resolves each declared section's REAL Shopify section id at
      // request time by reading the live DOM's own data-id attribute
      // when present. This matters because JSON-template-placed
      // sections (the cart PAGE's main-cart-items/main-cart-footer)
      // carry their true dynamic section id there — the same value
      // cart.js's own CartItems class reads via
      // document.getElementById(...).dataset.id — sending the literal
      // "main-cart-items" string as the Shopify section identifier
      // instead would silently return no data for that key, since it
      // wouldn't match Shopify's real internal section id. Phantom,
      // AJAX-only sections (the drawer's cart-notification-* files)
      // have no data-id and aren't placed via any template, so their
      // literal id string IS already their real section id — falling
      // back to that covers them correctly with the same code path.
      resolveSections() {
        return this.getSectionsToRender().map((section) => {
          const outer = document.getElementById(section.id);
          const requestId = (outer && outer.dataset && outer.dataset.id) || section.id;
          return { ...section, outer, requestId };
        });
      }

      getActiveDiscountCodes() {
        return (this.dataset.activeCodes || '')
          .split(',')
          .map((code) => code.trim())
          .filter(Boolean);
      }

      applyDiscountCode(rawCode) {
        const code = (rawCode || '').trim();
        if (!code) return;

        const activeCodes = this.getActiveDiscountCodes();
        if (activeCodes.some((existing) => existing.toLowerCase() === code.toLowerCase())) {
          this.showMessage(this.dataset.strAlreadyApplied || '', true);
          return;
        }

        const applyButton = this.querySelector('.cart-notification-discount__apply');
        if (applyButton) {
          applyButton.disabled = true;
          applyButton.textContent = this.dataset.strApplying || applyButton.textContent;
        }

        this.updateDiscount([...activeCodes, code].join(','), code);
      }

      removeDiscountCode(title) {
        if (!title) return;
        const remaining = this.getActiveDiscountCodes().filter(
          (existing) => existing.toLowerCase() !== title.toLowerCase()
        );
        this.updateDiscount(remaining.join(','), null);
      }

      // justAppliedCode is only set on an apply (not a remove) — used
      // to check whether that SPECIFIC code actually took effect,
      // since /cart/update.js doesn't return an explicit "invalid
      // code" error the way it does for e.g. quantity/inventory
      // errors — an invalid code is simply absent from the response's
      // cart_level_discount_applications, which is the only reliable
      // way to detect it.
      updateDiscount(discountCodesString, justAppliedCode) {
        const resolved = this.resolveSections();
        const body = JSON.stringify({
          discount: discountCodesString,
          sections: resolved.map((section) => section.requestId),
          sections_url: window.location.pathname,
        });

        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
          .then((response) => response.json())
          .then((parsedState) => {
            // Matches cart.js's own CartItems pattern exactly: `id`
            // identifies the section's root wrapper, `selector` (when
            // given) identifies a nested element WITHIN that root to
            // replace instead of the whole thing — needed for the cart
            // page, where #main-cart-footer's updatable content is
            // nested inside a .js-contents div (so the checkout
            // button/etc. outside it survive the swap), unlike the
            // drawer's flat single-purpose section divs, where the
            // selector simply won't match anything live and this
            // correctly falls back to the outer element itself.
            resolved.forEach((section) => {
              if (!section.outer) return;
              const sourceHtml = parsedState.sections[section.requestId];
              if (!sourceHtml) return;
              const selector = section.selector || '.shopify-section';
              const target = section.outer.querySelector(selector) || section.outer;
              const sourceRoot = new DOMParser().parseFromString(sourceHtml, 'text/html').querySelector(selector);
              target.innerHTML = sourceRoot ? sourceRoot.innerHTML : sourceHtml;
            });

            // This element itself may have been inside one of the
            // sections just replaced above (it usually is — the
            // discount form lives inside the pricing summary) — `this`
            // is then a detached node, so the message has to be set on
            // the FRESH instance the innerHTML swap just created.
            if (justAppliedCode) {
              const fresh = document.querySelector('cart-discount-form') || this;
              const applications = parsedState.cart_level_discount_applications || [];
              const applied = applications.some(
                (application) => (application.title || '').toLowerCase() === justAppliedCode.toLowerCase()
              );
              if (applied) {
                const successTemplate = fresh.dataset.strSuccess || '';
                fresh.showMessage(successTemplate.replace('[code]', justAppliedCode), false);
              } else {
                fresh.showMessage(fresh.dataset.strInvalid || '', true);
              }
            }

            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-discount-form', cartData: parsedState });
            }
          })
          .catch((error) => console.error(error));
      }

      showMessage(text, isError) {
        const messageEl = this.querySelector('.cart-notification-discount__message');
        if (!messageEl) return;
        messageEl.textContent = text;
        messageEl.classList.toggle('cart-notification-discount__message--error', !!isError);
        messageEl.classList.toggle('cart-notification-discount__message--success', !isError);
      }
    }
  );
}
